import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { usePagedListState } from '@/hooks'

import { LoadingPanel } from './LoadingPanel/LoadingPanel'
import { ListPagination } from './ListPagination/ListPagination'
import { DataListItem } from './DataListItem/DataListItem'
import { TableNoDataRow } from './TableNoDataRow/TableNoDataRow'

export type DataListProps<T> = {
  titles: string[]
  emptyList: string
  loadingLabel: string
  useSearchList: (
    page: number,
    size: number,
  ) => {
    data: { items: T[]; total: number } | undefined
    isFetching: boolean
    isUninitialized: boolean
  }
}

export const DataList = <T extends { id: string; code?: string; name?: string }>({
  titles,
  emptyList,
  loadingLabel,
  useSearchList,
}: DataListProps<T>): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const { page, size, onPageChange, onSizeChange } = usePagedListState({ initialFilters: {} })
  const { data: list = { items: [], total: 0 }, isFetching, isUninitialized } = useSearchList(page, size)
  const isLoading = isFetching || isUninitialized

  return (
    <LoadingPanel loading={isLoading} label={loadingLabel} key={i18n.language}>
      <Box sx={{ minWidth: 1050, mt: 3 }}>
        <TableContainer sx={{ borderRadius: '10px' }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#f8f8fa', borderBottom: '0px solid' }}>
              <TableRow>
                {titles.map((title) => (
                  <TableCell key={title}>{title}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {list.items.length === 0 ? (
                <TableNoDataRow colSpan={titles.length} message={emptyList} />
              ) : (
                list.items.map((object) => (
                  <TableRow hover key={object.id}>
                    <TableCell>
                      <DataListItem
                        data={object}
                        objectType={object.code ? `${object.code}` : `${object.id}`}
                        nameItem={object.code ? `${object.code} - ${object.name}` : `${object.name}`}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          mt: 2,
          px: 1,
        }}>
        {(!list.total || list.total <= 25) && (
          <Typography sx={{ fontSize: '14px', mr: 2 }}>
            {t('item1')} {list.total} {t('item2')}
          </Typography>
        )}

        <Box sx={{ flex: 1 }}>
          <ListPagination
            page={page}
            pageCount={Math.ceil(list.total / size)}
            onPageChange={onPageChange}
            pageSize={size}
            onPageSizeChange={onSizeChange}
          />
        </Box>
      </Stack>
    </LoadingPanel>
  )
}
